// Enhanced Directional Scale Partisan Score Calculation (Signed System)
export const calculatePartisanScore = (bill) => {
  // 1. Identify the Final Major Floor Vote
  let finalVote = null;
  if (bill.vote_breakdowns && bill.vote_breakdowns.length > 0) {
    // Find votes with final passage/third reading/passed
    const finalVotes = bill.vote_breakdowns.filter(vb => {
      const desc = (vb.description || '').toLowerCase();
      return desc.includes('final passage') || desc.includes('third reading') || desc.includes('passed');
    });
    // Use the latest one if multiple
    if (finalVotes.length > 0) {
      finalVote = finalVotes.reduce((latest, vb) => {
        if (!latest) return vb;
        const d1 = new Date(latest.date || 0);
        const d2 = new Date(vb.date || 0);
        return d2 > d1 ? vb : latest;
      }, null);
    }
  }
  // If not found, fallback to most recent vote
  if (!finalVote && bill.vote_breakdowns && bill.vote_breakdowns.length > 0) {
    finalVote = bill.vote_breakdowns.reduce((latest, vb) => {
      if (!latest) return vb;
      const d1 = new Date(latest.date || 0);
      const d2 = new Date(vb.date || 0);
      return d2 > d1 ? vb : latest;
    }, null);
  }

  // 2. Calculate Sponsor Score (signed)
  let sponsorScore = 0;
  let democratSponsors = 0, republicanSponsors = 0;
  if (bill.sponsors && bill.sponsors.length > 0) {
    bill.sponsors.forEach(s => {
      const party = (s.party || '').toUpperCase();
      if (party === 'D' || party === 'DEM' || party === 'DEMOCRAT') democratSponsors++;
      else if (party === 'R' || party === 'REP' || party === 'REPUBLICAN') republicanSponsors++;
    });
    const total = democratSponsors + republicanSponsors;
    if (total > 0) {
      const democratPercent = (democratSponsors / total) * 100;
      const republicanPercent = (republicanSponsors / total) * 100;
      // Signed score: negative for Democratic, positive for Republican
      sponsorScore = republicanPercent - democratPercent;
    }
  }

  // 3. Calculate Vote Score (signed)
  let voteScore = 0;
  let demYeaPercent = 0, repYeaPercent = 0;
  let hasVoteData = false;
  if (finalVote && finalVote.partyVoteCounts) {
    const dem = finalVote.partyVoteCounts.Democrat || {};
    const rep = finalVote.partyVoteCounts.Republican || {};
    const demTotal = (dem.Yea || 0) + (dem.Nay || 0) + (dem.NV || 0) + (dem.Absent || 0) + (dem.Excused || 0);
    const repTotal = (rep.Yea || 0) + (rep.Nay || 0) + (rep.NV || 0) + (rep.Absent || 0) + (rep.Excused || 0);
    
    if (demTotal > 0 && repTotal > 0) {
      demYeaPercent = (dem.Yea || 0) / demTotal * 100;
      repYeaPercent = (rep.Yea || 0) / repTotal * 100;
      // Signed score: negative for Democratic, positive for Republican
      voteScore = repYeaPercent - demYeaPercent;
      hasVoteData = true;
    }
  }

  // 4. Combine for the Final Partisan Score (signed)
  let partisanScore;
  if (hasVoteData) {
    partisanScore = 0.5 * sponsorScore + 0.5 * voteScore;
  } else {
    partisanScore = sponsorScore;
  }
  
  const roundedScore = Math.round(partisanScore * 10) / 10;
  const absoluteScore = Math.abs(roundedScore);

  // 5. Determine Direction from Sign
  let direction = 'Neutral';
  if (roundedScore < 0) {
    direction = 'Democratic';
  } else if (roundedScore > 0) {
    direction = 'Republican';
  }

  // 6. Enhanced Bucketed Label System (based on absolute score)
  let label = '';
  let leanStrength = '';
  // Only absolute values 0-10 are bipartisan
  if (absoluteScore <= 10) {
    leanStrength = 'Bipartisan';
    label = 'Bipartisan';
  } else if (absoluteScore <= 30) {
    leanStrength = 'Leaning';
    if (direction === 'Democratic') {
      label = 'Leaning Democrat';
    } else if (direction === 'Republican') {
      label = 'Leaning Republican';
    } else {
      label = 'Leaning Neutral';
    }
  } else if (absoluteScore <= 60) {
    leanStrength = 'Moderately';
    if (direction === 'Democratic') {
      label = 'Moderately Democrat';
    } else if (direction === 'Republican') {
      label = 'Moderately Republican';
    } else {
      label = 'Mixed Partisan';
    }
  } else if (absoluteScore <= 80) {
    leanStrength = 'Strongly';
    if (direction === 'Democratic') {
      label = 'Strongly Democrat';
    } else if (direction === 'Republican') {
      label = 'Strongly Republican';
    } else {
      label = 'Mixed Partisan';
    }
  } else {
    leanStrength = 'Highly';
    if (direction === 'Democratic') {
      label = 'Highly Democrat';
    } else if (direction === 'Republican') {
      label = 'Highly Republican';
    } else {
      label = 'Mixed Partisan';
    }
  }

  return {
    score: absoluteScore, // Return absolute value for display
    signedScore: roundedScore, // Keep signed version for internal use
    label,
    direction,
    leanStrength,
    sponsorScore: Math.round(sponsorScore * 10) / 10,
    voteScore: hasVoteData ? Math.round(voteScore * 10) / 10 : null,
    democratSponsors,
    republicanSponsors,
    demYeaPercent: hasVoteData ? Math.round(demYeaPercent * 10) / 10 : null,
    repYeaPercent: hasVoteData ? Math.round(repYeaPercent * 10) / 10 : null,
    hasVoteData
  };
}; 